import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import AuthContext from "./UserContext.js";
import GlobalStyles from "./styles/GlobalStyles";
import GlobalStylesComponent from "./styles/GlobalStylesComponent";
import FontAwesomeStyles from "./styles/FontAwesomeStyles";
import ToastifyStyles from "./styles/ToastifyStyles";
import DatepickerStyles from "./styles/DatepickerStyles";
import styled from "styled-components";
import RoleGuard from "./Componenets/Common/RoleGuard";
import {
  ADMIN_ROLES,
  NON_ADMIN_ROLES,
  AMILA_ROLES_TO_SEE_REPORT,
  CIRCUIT_ROLES,
  REPORT_HISTORY_ROLES,
  REPORT_SETTINGS_ROLES,
  GENERAL_REPORT_ROLES,
  ALL_ROLES
} from "./Services/roleUtils";

// App container styled component (replacing App.css)
const AppContainer = styled.div`
  display: flex;
`;

const MainContent = styled.div`
  flex-grow: 1;
  background-color: #f5f5f5;
`;

const LayoutContainer = styled.div`
  display: flex;
`;

const SidebarContainer = styled.div`
  position: relative;
  z-index: 2;
`;

const StyledMainContent = styled.div`
  flex-grow: 1;
  padding: 20px;
  transition: margin-left 0.3s ease;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

// Loading component with minimal UI impact
const LoadingFallback = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    backgroundColor: 'transparent'
  }}>
    <div style={{ width: '50px', height: '50px' }} />
  </div>
);

// Lazy load all components
const Login = React.lazy(() => import("./Componenets/Login/Login"));
const Home = React.lazy(() => import("./Componenets/Home/Home"));
const Layout = React.lazy(() => import("./Componenets/Layout/Layout.js"));
const ReportSubmission = React.lazy(() => import("./Componenets/ReportSubmission/ReportSubmission.jsx"));
const EditReportSubmission = React.lazy(() => import("./Componenets/ReportSubmission/EditReportSubmission.jsx"));
const Report = React.lazy(() => import("./Componenets/ReportPage/Report"));
const AddQuestion = React.lazy(() => import("./Componenets/Question/AddQuestion.jsx"));
const SubmissionWindow = React.lazy(() => import("./Componenets/SubmissionWindow/SubmissionWindows.jsx"));
const ReportTypeForm = React.lazy(() => import("./Componenets/ReportType/CreateReportType.jsx"));
const Questions = React.lazy(() => import("./Componenets/Question/Questions.jsx"));
const EditReportSection = React.lazy(() => import("./Componenets/ReportTypeSection/EditReportSection.jsx"));
const ReportSections = React.lazy(() => import("./Componenets/ReportTypeSection/ReportSections.jsx"));
const EditReportTypeForm = React.lazy(() => import("./Componenets/ReportType/EditReportTypeForm.jsx"));
const SubmissionWindows = React.lazy(() => import("./Componenets/SubmissionWindow/SubmissionWindows.jsx"));
const EditSubmissionWindow = React.lazy(() => import("./Componenets/SubmissionWindow/EditSubmissionWindow.jsx"));
const CreateSubmissionWindow = React.lazy(() => import("./Componenets/SubmissionWindow/CreateSubmissionWindow.jsx"));
const EditQuestion = React.lazy(() => import("./Componenets/Question/EditQuestion.jsx"));
const ReportType = React.lazy(() => import("./Componenets/ReportType/reportType.jsx"));
const JamaatReport = React.lazy(() => import("./Componenets/ReportPage/JamaatReport.jsx"));
const ViewReport = React.lazy(() => import("./Componenets/ReportPage/ViewReport.jsx"));
const MemberReportType = React.lazy(() => import("./Componenets/ReportType/MemberReportType.jsx"));
const AdminSettings = React.lazy(() => import("./Componenets/Sidebar/AdminSettings.jsx"));
const JamaatReportSubmission = React.lazy(() => import("./Componenets/ReportSubmission/JamaatReportSubmission.jsx"));
const GeneralReport = React.lazy(() => import("./Componenets/SubmissionWindow/AdminSubmissionWindows.jsx"));
const ViewCircuitReport = React.lazy(() => import("./Componenets/ReportPage/ViewCircuitReport.jsx"));
const CircuitAggregateEdit = React.lazy(() => import("./Componenets/ReportPage/CircuitAggregateEdit.jsx"));
const RoleManagement = React.lazy(() => import("./Componenets/RoleManagement/RoleManagement.jsx"));

// Wrap component with Suspense
const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={<LoadingFallback />}>
    {children}
  </Suspense>
);

// Wrap Layout component with Suspense
const LayoutWrapper = ({ children }) => (
  <SuspenseWrapper>
    <Layout>
      <SuspenseWrapper>
        {children}
      </SuspenseWrapper>
    </Layout>
  </SuspenseWrapper>
);

// Role-protected route wrapper
const ProtectedRoute = ({ element, allowedRoles }) => (
  <RoleGuard allowedRoles={allowedRoles} redirectTo="/Home">
    {element}
  </RoleGuard>
);

const App = () => {
  return (
    <AuthContext>
      <GlobalStyles />
      <GlobalStylesComponent />
      <FontAwesomeStyles />
      <ToastifyStyles />
      <DatepickerStyles />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public route */}
          <Route path="/" element={<SuspenseWrapper><Login /></SuspenseWrapper>} />
          
          {/* Home route - accessible to all authenticated users */}
          <Route path="/Home" element={<LayoutWrapper><Home /></LayoutWrapper>} />
          
          {/* Report History - accessible to CIRCUIT_ROLES only */}
          <Route path="/Report" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={CIRCUIT_ROLES} redirectTo="/Home">
                <Report />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Report submission routes - accessible to all roles */}
          <Route path="/ReportType/ReportSubmission" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ALL_ROLES} redirectTo="/Home">
                <ReportSubmission />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          <Route path="/ReportType/EditReportSubmission" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ALL_ROLES} redirectTo="/Home">
                <EditReportSubmission />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Jamaat Report Submission - accessible to all roles */}
          <Route path="/ReportSubmission/JamaatSubmissions" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ALL_ROLES} redirectTo="/Home">
                <JamaatReportSubmission />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Report details - accessible to all roles */}
          <Route path="/reportdetail" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={[...ADMIN_ROLES, ...CIRCUIT_ROLES, ...AMILA_ROLES_TO_SEE_REPORT, ...NON_ADMIN_ROLES]} redirectTo="/Home">
                <ViewReport />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Circuit report details - accessible to CIRCUIT_ROLES and ADMIN_ROLES */}
          <Route path="/circuit/reportdetail" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={[...ADMIN_ROLES, ...CIRCUIT_ROLES]} redirectTo="/Home">
                <ViewCircuitReport />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Circuit Aggregate Edit - accessible only to NON_ADMIN_ROLES */}
          <Route path="/circuit/aggregate/edit" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={NON_ADMIN_ROLES} redirectTo="/Home">
                <CircuitAggregateEdit />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Admin settings - accessible only to ADMIN_ROLES */}
          <Route path="/AdminSettings" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <AdminSettings />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Report Type management - accessible only to ADMIN_ROLES */}
          <Route path="/ReportType" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <ReportType />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Question routes - accessible only to ADMIN_ROLES */}
          <Route path="/Question/QuestionReport/:reportSectionId" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <AddQuestion />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Report Type Form routes - accessible only to ADMIN_ROLES */}
          <Route path="/ReportType/ReportTypeForm" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <ReportTypeForm />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          <Route path="/ReportType/CreateReportType" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <ReportTypeForm />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Jamaat Report - accessible to all roles */}
          <Route path="/JamaatReport" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ALL_ROLES} redirectTo="/Home">
                <JamaatReport />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Report Sections - accessible only to ADMIN_ROLES */}
          <Route path="/ReportType/ReportSections" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <ReportSections />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          <Route path="/Question/QuestionReport" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <Questions />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Submission Windows - accessible only to ADMIN_ROLES */}
          <Route path="/SubmissionWindows" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <SubmissionWindows />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Role Management - accessible only to ADMIN_ROLES */}
          <Route path="/RoleManagement" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <RoleManagement />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* General Report - accessible to AMILA_ROLES_TO_SEE_REPORT, CIRCUIT_ROLES, and NON_ADMIN_ROLES */}
          <Route path="/GeneralReport" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={[...AMILA_ROLES_TO_SEE_REPORT, ...CIRCUIT_ROLES, ...NON_ADMIN_ROLES]} redirectTo="/Home">
                <GeneralReport />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          {/* Other admin routes - accessible only to ADMIN_ROLES */}
          <Route path="/ReportType/ReportSections/Question/:reportSectionId" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <AddQuestion />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          <Route path="/SubmissionWindows/EditSubmissionWindow" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <EditSubmissionWindow />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          <Route path="/ReportType/EditReportTypeForm" element={
            <LayoutWrapper>
              <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                <EditReportTypeForm />
              </RoleGuard>
            </LayoutWrapper>
          } />
          
          <Route
            path="/ReportType/ReportSections/EditReportSection/:reportSectionId"
            element={
              <LayoutWrapper>
                <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                  <EditReportSection />
                </RoleGuard>
              </LayoutWrapper>
            }
          />
          
          <Route
            path="/ReportType/CreateSubmissionWindow"
            element={
              <LayoutWrapper>
                <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                  <CreateSubmissionWindow />
                </RoleGuard>
              </LayoutWrapper>
            }
          />
          
          <Route
            path="/ReportType/ReportSections/QuestionReport/:sectionName/:reportSectionId"
            element={
              <LayoutWrapper>
                <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                  <Questions />
                </RoleGuard>
              </LayoutWrapper>
            }
          />
          
          <Route
            path="/ReportType/ReportSections/QuestionReport/EditQuestion/:questionId"
            element={
              <LayoutWrapper>
                <RoleGuard allowedRoles={ADMIN_ROLES} redirectTo="/Home">
                  <EditQuestion />
                </RoleGuard>
              </LayoutWrapper>
            }
          />
          
          {/* Member Reports - accessible to CIRCUIT_ROLES only */}
          <Route
            path="/MemberReportType"
            element={
              <LayoutWrapper>
                <RoleGuard allowedRoles={CIRCUIT_ROLES} redirectTo="/Home">
                  <MemberReportType />
                </RoleGuard>
              </LayoutWrapper>
            }
          />
        </Routes>
      </Router>
    </AuthContext>
  );
};

export default App;
